import { useEffect, useMemo, useState } from "react";
import client from "../../../api/camasirhaneApi";
import { useAuth } from "../context/CamasirhaneRoleContext";
import { usePeriodStatus } from "../hooks/usePeriodStatus";
import { canWrite, isAdmin } from "../utils/auth";
import {
  MONTHS,
  YEARS,
  daysInMonth,
  formatNum,
  getIronMeasurementLevel,
} from "../utils/constants";
import "../styles/shared.css";

function parseIronNormalized(raw) {
  if (raw == null || raw === "") return null;
  const rawStr = String(raw).trim();
  if (!rawStr) return null;

  const low = rawStr.toLowerCase();
  if (/görülmedi|gorulmedi|yok|none|demir yok/.test(low) || rawStr === "0") return 0;

  const rangeMatch = rawStr.match(/^(\d+[.,]?\d*)\s*-\s*(\d+[.,]?\d*)$/);
  if (rangeMatch) {
    const lowVal = parseFloat(rangeMatch[1].replace(",", "."));
    const highVal = parseFloat(rangeMatch[2].replace(",", "."));
    if (Number.isFinite(lowVal) && Number.isFinite(highVal)) return (lowVal + highVal) / 2;
  }

  const numeric = parseFloat(rawStr.replace(",", "."));
  return Number.isFinite(numeric) ? numeric : null;
}

function getNormalizedValue(record, kind) {
  const normKey = kind === "laundry" ? "laundry_value_normalized" : "boiler_or_hardness_normalized";
  const rawKey = kind === "laundry" ? "laundry_value_raw" : "boiler_or_hardness_raw";
  const stored = record?.[normKey];
  if (stored != null && stored !== "") {
    const numeric = Number(stored);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return parseIronNormalized(record?.[rawKey]);
}

function ironCellProps(level) {
  if (!level) return {};
  return {
    className: `iron-value-level iron-value-level-${level}`,
  };
}

function ironLevelClass(level) {
  return level
    ? `iron-normalized iron-value-level iron-value-level-${level}`
    : "iron-normalized";
}

export default function IronMeasurementScreen() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const admin = isAdmin(user);

  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(7);
  const [records, setRecords] = useState({});
  const [originalDays, setOriginalDays] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const { isClosed } = usePeriodStatus(year, month);
  const readOnly = !writable || (isClosed && !admin);

  const days = useMemo(() => Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1), [year, month]);

  useEffect(() => {
    setRecords({});
    setOriginalDays(new Set());
    client.get("/iron-measurements/", { params: { year, month } })
      .then((res) => {
        const next = {};
        const loadedDays = new Set();
        res.data.forEach((rec) => {
          loadedDays.add(rec.day);
          next[rec.day] = {
            laundry_value_raw: rec.laundry_value_raw ?? "",
            boiler_or_hardness_raw: rec.boiler_or_hardness_raw ?? "",
            note: rec.note ?? "",
            laundry_value_normalized: rec.laundry_value_normalized,
            boiler_or_hardness_normalized: rec.boiler_or_hardness_normalized,
          };
        });
        setRecords(next);
        setOriginalDays(loadedDays);
      }).catch(() => {});
  }, [year, month]);

  function update(day, field, value) {
    setRecords((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMessage("");
    const payload = [];

    days.forEach((day) => {
      const row = records[day] || {};
      const laundry = row.laundry_value_raw ?? "";
      const boiler = row.boiler_or_hardness_raw ?? "";
      const note = row.note ?? "";
      const hasValue = Boolean(laundry || boiler || note);

      if (hasValue) {
        payload.push({ year, month, day, laundry_value_raw: laundry, boiler_or_hardness_raw: boiler, note });
        return;
      }
      if (originalDays.has(day)) {
        payload.push({ year, month, day, delete: true });
      }
    });

    if (!payload.length) {
      setSaveMessage("Kaydedilecek değişiklik yok.");
      setSaving(false);
      return;
    }

    try {
      await client.post("/iron-measurements/bulk/", { records: payload });
      setSaveMessage("Kaydedildi.");
      const res = await client.get("/iron-measurements/", { params: { year, month } });
      const next = {};
      const loadedDays = new Set();
      res.data.forEach((rec) => {
        loadedDays.add(rec.day);
        next[rec.day] = {
          laundry_value_raw: rec.laundry_value_raw ?? "",
          boiler_or_hardness_raw: rec.boiler_or_hardness_raw ?? "",
          note: rec.note ?? "",
          laundry_value_normalized: rec.laundry_value_normalized,
          boiler_or_hardness_normalized: rec.boiler_or_hardness_normalized,
        };
      });
      setRecords(next);
      setOriginalDays(loadedDays);
    } catch (err) {
      setSaveMessage(err.response?.data?.detail || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Demir Ölçüm</h2>
        <p>Günlük çamaşırhane demir değeri ve su sertliği takibi</p>
      </div>

      {isClosed && <div className="locked-banner">🔒 Bu dönem kapalı.</div>}

      <div className="toolbar">
        <select className="ui-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
        </select>
        <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {!readOnly && (
          <button className="ui-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        )}
        {saveMessage && <span className="status-msg success">{saveMessage}</span>}
      </div>

      <div className="grid-wrapper">
        <table className="data-grid data-grid--iron">
          <thead>
            <tr>
              <th className="sticky-col label-col">Gün</th>
              <th>Çamaşırhane Demir</th>
              <th>Normalize</th>
              <th>Kazan / Sertlik</th>
              <th>Normalize</th>
              <th>Açıklama</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => {
              const row = records[day] || {};
              const band = Math.floor((day - 1) / 2);
              const laundryValue = getNormalizedValue(row, "laundry");
              const boilerValue = getNormalizedValue(row, "boiler");
              const laundryLevel = getIronMeasurementLevel(row.laundry_value_raw);
              const boilerLevel = getIronMeasurementLevel(row.boiler_or_hardness_raw);
              const laundryCell = ironCellProps(laundryLevel);
              const boilerCell = ironCellProps(boilerLevel);

              return (
              <tr key={day} className="iron-data-row" style={{ "--iron-band": band }}>
                <td className="sticky-col label-col">{day}</td>
                <td {...laundryCell}>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={row.laundry_value_raw ?? ""}
                    onChange={(e) => update(day, "laundry_value_raw", e.target.value)}
                  />
                </td>
                <td className={ironLevelClass(laundryLevel)}>
                  {row.laundry_value_normalized ?? (laundryValue != null ? formatNum(laundryValue, 4) : "-")}
                </td>
                <td {...boilerCell}>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={row.boiler_or_hardness_raw ?? ""}
                    onChange={(e) => update(day, "boiler_or_hardness_raw", e.target.value)}
                  />
                </td>
                <td className={ironLevelClass(boilerLevel)}>
                  {row.boiler_or_hardness_normalized ?? (boilerValue != null ? formatNum(boilerValue, 4) : "-")}
                </td>
                <td>
                  <input type="text" disabled={readOnly}
                    value={row.note ?? ""}
                    onChange={(e) => update(day, "note", e.target.value)} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
